import React from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PmsSettings, CredentialsStatus, PmsRecentActivity } from "@/types/pms";
import { PmsType } from "@/types/hotel";
import { PMS_PROVIDER_LABEL_KEYS } from "@/utils/pmsProviderLabels";
import { formatRelativeTime } from "@/utils/dateUtils";

interface ConnectionStatusCardProps {
  settings: PmsSettings | null;
  credentials: CredentialsStatus | null;
  recentActivity?: PmsRecentActivity;
}

export const ConnectionStatusCard = ({
  settings,
  credentials,
  recentActivity,
}: ConnectionStatusCardProps) => {
  const { t } = useTranslation();

  const providerLabelKey = settings?.provider
    ? PMS_PROVIDER_LABEL_KEYS[settings.provider as PmsType]
    : undefined;
  const providerLabel = providerLabelKey
    ? t(providerLabelKey)
    : settings?.provider;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle>{t("pms.status.title")}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap items-center gap-3">
          {settings?.provider && (
            <Badge variant="new_secondary">{providerLabel}</Badge>
          )}
          {settings?.enabled ? (
            <Badge variant="filled_status_completed">
              {t("pms.status.active")}
            </Badge>
          ) : (
            <Badge variant="new_destructive">{t("pms.status.disabled")}</Badge>
          )}
          {credentials?.hasCredentials ? (
            <Badge variant="filled_status_completed">
              {t("pms.status.configured")}
            </Badge>
          ) : (
            <Badge variant="new_outline">{t("pms.status.notSet")}</Badge>
          )}
          {credentials?.hasAdvancedConfig && (
            <Badge variant="filled_status_completed">
              {t("pms.status.advancedConfigStored")}
            </Badge>
          )}
        </div>
        {settings?.enabled && (
          <div className="text-muted-foreground mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm">
            {settings.pmsHotelId && (
              <span>
                {t("pms.status.pmsHotelId")}: {settings.pmsHotelId}
              </span>
            )}
            <span>
              {t("pms.status.pollInterval")}:{" "}
              {t("pms.status.everyMinutes", {
                minutes: settings.pollIntervalMinutes,
              })}
            </span>
            <span>
              {t("pms.status.lastInbound")}:{" "}
              {formatRelativeTime(settings.lastInboundAt) ||
                t("pms.status.never")}
            </span>
            <span>
              {t("pms.status.lastOutbound")}:{" "}
              {formatRelativeTime(settings.lastOutboundAt) ||
                t("pms.status.never")}
            </span>
            {recentActivity && (
              <>
                <span>
                  {t("pms.status.reservationsInWindow", {
                    hours: recentActivity.windowHours,
                  })}
                  : {recentActivity.reservations}
                </span>
                <span>
                  {t("pms.status.roomsInWindow", {
                    hours: recentActivity.windowHours,
                  })}
                  : {recentActivity.rooms}
                </span>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
